import * as THREE from "three";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { PerspectiveCamera, useProgress } from "@react-three/drei";
import { easing } from "maath";
import Experience from "./Experience";
import { Perf } from "r3f-perf";
import { useState, useRef, useEffect } from "react";
import LoadingOverlay from "./LoadingOverlay";
import ProjectFocusCard from "./projects/ProjectFocusCard";
import { useSceneReady } from "@/stores/sceneReadyStore";
import { useModelCompileTick } from "@/stores/modelCompileStore";
import { useProjectFocus } from "@/stores/projectFocusStore";
import { FOCUS_DIR, FOCUS_RIG_TARGET, focusPanelWidth } from "./focusUtils";

// True once assets are loaded AND no model has finished compiling for 1.5s
// (models ping the compile store as they appear). Drives the frosted-glass
// loading overlay; 10s safety cap so it can never stick.
function useSceneSettled() {
  const { active, progress } = useProgress();
  const tick = useModelCompileTick((s) => s.tick);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (settled || active || progress !== 100) return undefined;
    const timer = setTimeout(() => setSettled(true), 1500);
    return () => clearTimeout(timer);
  }, [active, progress, tick, settled]);

  useEffect(() => {
    const cap = setTimeout(() => setSettled(true), 10000);
    return () => clearTimeout(cap);
  }, []);

  return settled;
}

// Runs the startup warm-up behind the loading overlay. First use of each
// shader program forces the ANGLE driver to finish compiling it
// synchronously (~70 programs, seconds of stalls), so instead of revealing
// a janky scene we drive frames manually from the start:
// - Each tick, materials that newly mounted (models stream in over several
//   seconds) are batch-linked with gl.compile() so the driver compiles them
//   in parallel on worker threads; frames pause until the batch reports
//   ready via the non-blocking COMPLETION_STATUS query. Rendering earlier
//   would sync-wait on every program in one frame - a multi-second stall.
// - advance() renders real frames (shadow maps, postprocessing, temporal
//   shadow bake), compiling the few program variants gl.compile misses.
// Releases when assets are loaded and the main thread has been quiet for
// 600ms, capped at 6s so the scene can never stay frozen behind the overlay.
// (three's own compileAsync is avoided: in r163 it crashes when a compiled
// material ends up without currentProgram, leaving its promise pending.)
function CompileGate({ onReady }) {
  const gl = useThree((s) => s.gl);
  const scene = useThree((s) => s.scene);
  const camera = useThree((s) => s.camera);
  const setFrameloop = useThree((s) => s.setFrameloop);
  const advance = useThree((s) => s.advance);
  const { active, progress } = useProgress();
  const loadedRef = useRef(false);
  const loadedAtRef = useRef(0);
  if (!active && progress === 100 && !loadedRef.current) {
    loadedRef.current = true;
    loadedAtRef.current = performance.now();
  }

  useEffect(() => {
    let done = false;
    let timer;
    let lastLong = performance.now();
    const cap = performance.now() + 6000;
    const seen = new Set();

    const release = () => {
      if (done) return;
      done = true;
      setFrameloop("always");
      onReady();
    };

    // Materials that appeared in the scene graph since the last tick.
    const collectNewMaterials = () => {
      const fresh = [];
      scene.traverse((object) => {
        const material = object.material;
        if (!material) return;
        for (const mat of Array.isArray(material) ? material : [material]) {
          if (!seen.has(mat)) {
            seen.add(mat);
            fresh.push(mat);
          }
        }
      });
      return fresh;
    };

    const step = () => {
      if (done) return;
      const fresh = collectNewMaterials();
      if (fresh.length > 0) {
        try {
          gl.compile(scene, camera);
        } catch {
          // Ignore: materials that fail here compile on their first draw.
        }
        const pending = new Set(fresh);
        const deadline = performance.now() + 3000;
        const poll = () => {
          if (done) return;
          for (const material of [...pending]) {
            const entry = gl.properties.get(material);
            if (
              !entry ||
              !entry.currentProgram ||
              entry.currentProgram.isReady()
            ) {
              pending.delete(material);
            }
          }
          if (pending.size === 0 || performance.now() > deadline) {
            lastLong = performance.now();
            timer = setTimeout(step, 0);
          } else {
            timer = setTimeout(poll, 10);
          }
        };
        timer = setTimeout(poll, 10);
        return;
      }
      const before = performance.now();
      try {
        advance(before);
      } catch {
        // A failed warm-up render must not keep the scene frozen.
      }
      const now = performance.now();
      // A slow warm-up frame means a program just sync-compiled or a heavy
      // upload happened; reset the quiet clock. Measured directly because
      // PerformanceObserver delivers longtask entries too late for this loop.
      if (now - before > 50) lastLong = now;
      // Grace period after load-complete: embedded textures decode
      // asynchronously and their models mount 1-2s after useProgress hits
      // 100, so an early release would let that wave sync-compile in view.
      if (
        (loadedRef.current &&
          now - loadedAtRef.current > 2500 &&
          now - lastLong > 600) ||
        now > cap
      ) {
        release();
      } else {
        timer = setTimeout(step, 33);
      }
    };
    step();

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

// Wheel (desktop) and two-finger pinch (touch) adjust the focus framing
// while a model is focused. Window-level because OrbitControls is disabled
// during focus and must not receive these events anyway.
function useFocusViewZoom() {
  const focusActive = useProjectFocus((s) => Boolean(s.focused));

  useEffect(() => {
    if (!focusActive) return undefined;
    const clampZoom = (v) => Math.min(Math.max(v, 0.5), 3);
    const overCard = (e) =>
      e.target instanceof Element && e.target.closest("[data-focus-card]");
    const onWheel = (e) => {
      if (overCard(e)) return;
      e.preventDefault();
      const { viewZoom, setViewZoom } = useProjectFocus.getState();
      setViewZoom(clampZoom(viewZoom * Math.exp(-e.deltaY * 0.0015)));
    };
    let pinchDist = 0;
    const touchDist = (t) => Math.hypot(
      t[0].clientX - t[1].clientX,
      t[0].clientY - t[1].clientY,
    );
    const onTouchStart = (e) => {
      if (e.touches.length === 2) pinchDist = touchDist(e.touches);
    };
    const onTouchMove = (e) => {
      if (e.touches.length !== 2) return;
      if (overCard(e)) return;
      e.preventDefault();
      const d = touchDist(e.touches);
      if (pinchDist > 0) {
        const { viewZoom, setViewZoom } = useProjectFocus.getState();
        setViewZoom(clampZoom(viewZoom * (d / pinchDist)));
      }
      pinchDist = d;
    };
    const onTouchEnd = () => {
      pinchDist = 0;
    };
    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [focusActive]);
}

export default function Scene() {
  const setSceneReady = useSceneReady((s) => s.setReady);
  useFocusViewZoom();
  // Perspective camera that takes over during the focus view; the default
  // orthographic camera keeps the free-roam look.
  const focusCamRef = useRef();
  // Frosted-glass loading overlay: the scene renders (blurred) behind it
  // while models stream in and shaders compile; it fades once settled.
  const settled = useSceneSettled();

  useEffect(() => {
    if (settled) setSceneReady();
  }, [settled, setSceneReady]);

  return (
    <div className="absolute h-screen w-screen overflow-hidden">
      <LoadingOverlay isReady={settled} />

      <Canvas
        dpr={[1, 1.5]} // Limit max resolution for performance on 4K displays
        shadows
        // Skip per-program link-status/info-log queries on first use; they
        // add sync GL stalls. Keep them in dev so shader errors surface.
        onCreated={({ gl }) => {
          gl.debug.checkShaderErrors = import.meta.env.DEV;
        }}
        orthographic
        camera={{
          fov: 45,
          zoom: 50,
          near: -100,
          far: 300,
          position: [20, 20, 20],
        }}
        onPointerMissed={() => {
          const { focused, clearFocus } = useProjectFocus.getState();
          if (focused) clearFocus();
        }}
      >
        <color attach="background" args={["#15151a"]} />
        <fog attach="fog" args={["#15151a", 10, 100]} />
        <Experience />
        {/* <Perf position="top-right" colorBlind /> */}
        <PerspectiveCamera ref={focusCamRef} fov={FOCUS_FOV} near={0.5} far={400} />
        <CameraRig focusCamRef={focusCamRef} />
      </Canvas>

      <ProjectFocusCard />
    </div>
  );
}

const FOCUS_DIR_VEC = new THREE.Vector3(...FOCUS_DIR).normalize();
const UP = new THREE.Vector3(0, 1, 0);
// Mouse parallax in focus view: max extra orbit around the model, radians.
// Pitch stays below the 5° base elevation so the camera never dips under
// the ground plane.
const FOCUS_PARALLAX_YAW = 0.15;
const FOCUS_PARALLAX_PITCH = 0.06;
// Field of view of the perspective focus camera. The focus distance is
// derived from the stored ortho zoom so both cameras frame the model at the
// same on-screen size: dist = viewportHeight / (2 * zoom * tan(fov / 2)).
const FOCUS_FOV = 35;
const TAN_HALF_FOV = Math.tan((FOCUS_FOV * Math.PI) / 360);
// Fixed durations (seconds) for the fly-in and fly-back tweens.
const FOCUS_FLY_DURATION = 1.2;
const FOCUS_RETURN_DURATION = 1.0;

function easeInOutCubic(p) {
  return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
}

// Element-wise lerp between two projection matrices. Intermediate matrices
// are not true projections but blend visually, which is the standard trick
// for a smooth orthographic-to-perspective morph.
function lerpProjection(from, to, k, out) {
  const a = from.elements;
  const b = to.elements;
  const o = out.elements;
  for (let i = 0; i < 16; i++) o[i] = a[i] + (b[i] - a[i]) * k;
}

function CameraRig({ focusCamRef }) {
  const lastInteractionTime = useRef(0);
  const lastSetPos = useRef(new THREE.Vector3());
  // Free-roam view captured when entering project focus; restored on exit.
  // Lives here (not in the store) so project-to-project refocus keeps the
  // original view until the user fully exits.
  const savedView = useRef(null);
  const scratch = useRef({
    goalTarget: new THREE.Vector3(),
    goalPos: new THREE.Vector3(),
    dir: new THREE.Vector3(),
    right: new THREE.Vector3(),
    up: new THREE.Vector3(),
  });
  // One eased tween drives each flight: position, look-at target, zoom and
  // the ortho/perspective projection morph all follow the same progress, so
  // the camera moves in a straight line from the current view to the focus
  // view with no intermediate swoop. `src` holds the projection matrix at
  // the camera handoff; `id` detects refocusing to a different model.
  const flight = useRef({
    p: 1,
    id: null,
    fromPos: new THREE.Vector3(),
    fromTarget: new THREE.Vector3(),
    fromZoom: 0,
    src: new THREE.Matrix4(),
    tmp: new THREE.Matrix4(),
  });

  return useFrame((state, delta) => {
    const t = state.clock.elapsedTime;
    // Clamp so a hitch (shader compile, tab switch) cannot turn the smooth
    // damp into a teleport on the next frame.
    const dt = Math.min(delta, 0.05);
    const { camera, controls, size, set } = state;
    const focusCam = focusCamRef.current;
    const { focused, settled, setSettled, restoring, setRestoring, viewZoom } =
      useProjectFocus.getState();
    // Shared with OrbitControls (seeded on its remounts); rig-animated here.
    const target = FOCUS_RIG_TARGET;

    if (focused && focusCam) {
      const { goalTarget, goalPos, dir, right, up } = scratch.current;
      const f = flight.current;
      goalTarget.fromArray(focused.center);
      if (!savedView.current) {
        if (controls) target.copy(controls.target);
        savedView.current = {
          camera,
          target: target.clone(),
          position: camera.position.clone(),
          zoom: camera.zoom,
          near: camera.near,
        };
      }
      if (camera !== focusCam) {
        // Hand off to the perspective camera in place: same position and
        // orientation; the projection morphs along the flight (below).
        focusCam.position.copy(camera.position);
        focusCam.lookAt(target);
        f.src.copy(camera.projectionMatrix);
        set({ camera: focusCam });
        f.id = null; // force a fresh flight start below
      }
      if (f.id !== focused.id) {
        // New flight: from wherever the camera is right now to the focus
        // framing. Also covers refocusing a different model.
        f.id = focused.id;
        f.p = 0;
        f.fromPos.copy(focusCam.position);
        f.fromTarget.copy(target);
      }
      dir.copy(FOCUS_DIR_VEC);
      if (focused.yaw) dir.applyAxisAngle(UP, focused.yaw);
      // Cursor-driven parallax: small orbit of the goal with the pointer.
      dir.applyAxisAngle(UP, -state.pointer.x * FOCUS_PARALLAX_YAW);
      right.crossVectors(UP, dir).normalize();
      dir
        .applyAxisAngle(right, state.pointer.y * FOCUS_PARALLAX_PITCH)
        .normalize();
      // Perspective framing: distance, not zoom, sets the on-screen size.
      // viewZoom is the user's wheel/pinch zoom while focused; effZoom is
      // px per world unit at the focus plane.
      const effZoom = focused.zoom * viewZoom;
      const goalDist = size.height / (2 * effZoom * TAN_HALF_FOV);
      if (size.width >= 768) {
        // The info side panel covers the left edge on desktop; shift the
        // look-at left so the model centers in the remaining visible area.
        goalTarget.addScaledVector(
          right,
          -focusPanelWidth(size.width) / 2 / effZoom,
        );
      } else {
        // On mobile the info panel is a bottom sheet (46vh + fade): shift
        // the look-at down so the model centers in the visible area above.
        up.crossVectors(dir, right).normalize();
        goalTarget.addScaledVector(up, (-0.15 * size.height) / effZoom);
      }
      goalPos.copy(goalTarget).addScaledVector(dir, goalDist);

      focusCam.updateProjectionMatrix();
      if (f.p < 1) {
        f.p = Math.min(f.p + dt / FOCUS_FLY_DURATION, 1);
        const eased = easeInOutCubic(f.p);
        focusCam.position.lerpVectors(f.fromPos, goalPos, eased);
        target.lerpVectors(f.fromTarget, goalTarget, eased);
        if (eased < 0.999) {
          f.tmp.copy(focusCam.projectionMatrix);
          lerpProjection(f.src, f.tmp, eased, focusCam.projectionMatrix);
          focusCam.projectionMatrixInverse
            .copy(focusCam.projectionMatrix)
            .invert();
        }
      } else {
        // Arrived: keep following the parallax goal with a light damp.
        easing.damp3(target, goalTarget, 0.3, dt);
        easing.damp3(focusCam.position, goalPos, 0.3, dt);
      }
      focusCam.lookAt(target);
      if (controls) controls.target.copy(target);
      lastInteractionTime.current = t;

      if (!settled && f.p >= 1) {
        setSettled(true);
      }
      return;
    }

    if (savedView.current) {
      const saved = savedView.current;
      const ortho = saved.camera;
      const f = flight.current;
      if (camera !== ortho && focusCam) {
        // Hand back to the ortho camera where the perspective one stopped:
        // same position, zoom matched to the current frame size; the
        // projection morphs back along the return flight. Near stays at 0
        // so geometry behind the camera cannot flash in mid-flight.
        const dist = Math.max(focusCam.position.distanceTo(target), 0.001);
        ortho.position.copy(focusCam.position);
        ortho.zoom = size.height / (2 * dist * TAN_HALF_FOV);
        ortho.near = 0;
        ortho.updateProjectionMatrix();
        ortho.lookAt(target);
        f.src.copy(focusCam.projectionMatrix);
        f.id = null;
        f.p = 0;
        f.fromPos.copy(ortho.position);
        f.fromTarget.copy(target);
        f.fromZoom = ortho.zoom;
        set({ camera: ortho });
      }
      f.p = Math.min(f.p + dt / FOCUS_RETURN_DURATION, 1);
      const eased = easeInOutCubic(f.p);
      ortho.position.lerpVectors(f.fromPos, saved.position, eased);
      target.lerpVectors(f.fromTarget, saved.target, eased);
      ortho.zoom = f.fromZoom + (saved.zoom - f.fromZoom) * eased;
      ortho.updateProjectionMatrix();
      if (eased < 0.999) {
        f.tmp.copy(ortho.projectionMatrix);
        lerpProjection(f.src, f.tmp, eased, ortho.projectionMatrix);
        ortho.projectionMatrixInverse.copy(ortho.projectionMatrix).invert();
      }
      ortho.lookAt(target);
      if (controls) controls.target.copy(target);
      lastSetPos.current.copy(ortho.position);
      lastInteractionTime.current = t;
      if (f.p >= 1) {
        ortho.zoom = saved.zoom;
        ortho.near = saved.near;
        ortho.updateProjectionMatrix();
        // Sync OrbitControls' internal spherical state to the restored
        // camera before the next user drag.
        if (controls) controls.update();
        savedView.current = null;
        if (restoring) setRestoring(false);
      }
      return;
    }

    const freeTarget = controls?.target || target;

    // 1. Detect user interaction
    // If the camera position is different from where the rig last set it,
    // it means the user (OrbitControls) has moved it.
    if (camera.position.distanceTo(lastSetPos.current) > 0.001) {
      lastInteractionTime.current = t;
    }

    // 2. Only snap back if 3 seconds have passed since the last interaction
    if (t - lastInteractionTime.current > 3) {
      easing.damp3(
        camera.position,
        [
          Math.sin(t / 2) + 20 + freeTarget.x,
          20 + freeTarget.y,
          Math.cos(t / 2) / 2 + 20 + freeTarget.z,
        ],
        2.0,
        dt,
      );
    }

    // 3. Keep looking at the target and record our position to detect future user movement
    camera.lookAt(freeTarget);
    lastSetPos.current.copy(camera.position);
    target.copy(freeTarget);
  });
}
